import * as THREE from "three";
import { CSS2DObject } from "three/addons/renderers/CSS2DRenderer.js";
import {
  isAmbientWalkPos,
  type AmbientLeashWalkerDef,
  type MapData,
} from "@dogdell/shared";
import { humanSprite, humanWalkSprite } from "./CharacterSprites";
import { makeContactShadow } from "./ContactShadow";
import { DOG_BREEDS } from "./DogBreeds";
import { applyLabelLod, NPC_LABEL_FAR, NPC_LABEL_NEAR } from "./LabelLod";
import {
  billboardMaterial,
  faceCameraYaw,
  facingFlipSign,
  makeSilhouette,
  placeRimBehind,
  setSilhouetteMap,
  SPRITE_FOOT_Y,
  type WorldArt,
} from "./WorldArt";

const LEASH_LEN = 1.35;

/**
 * Ambient human strolling the lawn with a leashed pup.
 * Stays on walkable grass/paths — never ponds, stage deck, or travel pads.
 */
export class ParkLeashWalker {
  readonly group = new THREE.Group();
  private readonly human: THREE.Mesh;
  private readonly dog: THREE.Mesh;
  private readonly dogRim: THREE.Mesh;
  private readonly leash: THREE.Line;
  private readonly leashGeo: THREE.BufferGeometry;
  private readonly humanShadow: THREE.Mesh;
  private readonly dogShadow: THREE.Mesh;
  private readonly labelEl: HTMLDivElement;
  private readonly humanVariant: number;
  private readonly fur: number;
  private readonly homeX: number;
  private readonly homeY: number;
  private readonly roam: number;
  private readonly speed: number;
  private readonly id: string;
  private x: number;
  private y: number;
  private dogX: number;
  private dogY: number;
  private facing: number;
  private wanderAngle: number;
  private gaitPhase = 0;
  private dogFrame = 0;
  private walkStep = -1;
  private moving = false;
  private pauseUntil = 0;
  private readonly idleSheet: ReturnType<typeof humanSprite>;
  private readonly humanFootY: number;

  get poseX(): number {
    return this.x - 0.5;
  }

  get poseY(): number {
    return this.y - 0.5;
  }

  get dogPoseX(): number {
    return this.dogX - 0.5;
  }

  get dogPoseY(): number {
    return this.dogY - 0.5;
  }

  constructor(
    private readonly art: WorldArt,
    private readonly map: MapData,
    def: AmbientLeashWalkerDef,
    private readonly groundY: (poseX: number, poseY: number) => number,
  ) {
    this.id = def.id;
    this.humanVariant = def.humanVariant ?? Math.floor(hash(def.id + "hum") * 24);
    this.fur =
      def.dogFur ?? Math.floor(hash(def.id + "dog") * DOG_BREEDS.length);
    const home = snapAmbientHome(map, def.homeX + 0.5, def.homeY + 0.5);
    this.homeX = home.x;
    this.homeY = home.y;
    this.x = this.homeX;
    this.y = this.homeY;
    this.roam = def.roam ?? 14 + hash(def.id) * 8;
    this.speed = 0.95 + hash(def.id + "spd") * 0.55;
    this.wanderAngle = hash(def.id) * Math.PI * 2;
    this.facing = this.wanderAngle;
    this.pauseUntil = performance.now() + hash(def.id + "wait") * 2200;
    const back = this.wanderAngle + Math.PI;
    this.dogX = this.x + Math.cos(back) * LEASH_LEN * 0.85;
    this.dogY = this.y + Math.sin(back) * LEASH_LEN * 0.85;

    this.idleSheet = humanSprite("stand", this.humanVariant);
    this.humanFootY = this.idleSheet.footY ?? SPRITE_FOOT_Y;
    this.human = new THREE.Mesh(
      new THREE.PlaneGeometry(this.idleSheet.width, this.idleSheet.height),
      billboardMaterial(this.idleSheet.texture),
    );
    this.human.position.y = this.idleSheet.height * this.humanFootY;
    this.human.renderOrder = 3;
    this.group.add(this.human);

    const dogSize = art.dogSize(this.fur);
    const dogTex = art.dog(this.fur, null, 0, undefined, "idle");
    this.dogRim = makeSilhouette(dogSize.width, dogSize.height, dogTex);
    this.dogRim.position.y = dogSize.height * SPRITE_FOOT_Y;
    this.group.add(this.dogRim);
    this.dog = new THREE.Mesh(
      new THREE.PlaneGeometry(dogSize.width, dogSize.height),
      billboardMaterial(dogTex),
    );
    this.dog.position.y = dogSize.height * SPRITE_FOOT_Y;
    this.dog.renderOrder = 2;
    this.group.add(this.dog);

    this.leashGeo = new THREE.BufferGeometry();
    this.leashGeo.setAttribute(
      "position",
      new THREE.BufferAttribute(new Float32Array(6), 3),
    );
    this.leash = new THREE.Line(
      this.leashGeo,
      new THREE.LineBasicMaterial({
        color: 0x5d4037,
        transparent: true,
        opacity: 0.72,
        depthTest: true,
      }),
    );
    this.leash.renderOrder = 1;
    this.group.add(this.leash);

    this.humanShadow = makeContactShadow({
      radius: Math.min(0.32, this.idleSheet.width * 0.28),
      opacity: 0.42,
      y: 0.012,
    });
    this.dogShadow = makeContactShadow({
      radius: Math.min(0.26, dogSize.width * 0.32),
      opacity: 0.4,
      y: 0.012,
    });
    this.group.add(this.humanShadow, this.dogShadow);

    this.labelEl = document.createElement("div");
    this.labelEl.className = "world-label world-npc";
    this.labelEl.textContent = `${def.name} & ${def.dogName}`;
    const label = new CSS2DObject(this.labelEl);
    label.position.set(0, this.idleSheet.height + 0.12, 0);
    this.group.add(label);

    this.place();
  }

  update(dtSec: number, now: number, camera: THREE.Camera, focusX: number, focusZ: number): void {
    if (now < this.pauseUntil) {
      this.moving = false;
    } else {
      this.wanderAngle += (hash(this.id + Math.floor(now / 2600)) - 0.5) * dtSec * 1.4;
      const step = this.speed * dtSec;
      const moved = this.stepHuman(Math.cos(this.wanderAngle) * step, Math.sin(this.wanderAngle) * step);
      this.facing = this.wanderAngle;
      this.moving = moved;

      const homeDist = Math.hypot(this.x - this.homeX, this.y - this.homeY);
      if (homeDist > this.roam) {
        const ang = Math.atan2(this.homeY - this.y, this.homeX - this.x);
        this.wanderAngle = ang;
        this.stepHuman(Math.cos(ang) * this.speed * dtSec * 1.25, Math.sin(ang) * this.speed * dtSec * 1.25);
        this.facing = ang;
        this.moving = true;
      }

      if (hash(this.id + Math.floor(now / 5500)) > 0.78) {
        this.pauseUntil = now + 1100 + hash(this.id + "p") * 1600;
      }
    }

    this.followDog(dtSec);
    this.updateSprites(dtSec, now, camera);
    this.updateLeash();
    this.place();

    const dist = Math.hypot(this.x - focusX, this.y - focusZ);
    applyLabelLod(this.labelEl, dist, { near: NPC_LABEL_NEAR, far: NPC_LABEL_FAR });
  }

  destroy(): void {
    this.labelEl.remove();
    this.group.traverse((child) => {
      if (child instanceof CSS2DObject) {
        child.element.remove();
      }
      if (child instanceof THREE.Mesh) {
        child.geometry.dispose();
        if (child.material instanceof THREE.Material) {
          child.material.dispose();
        }
      }
      if (child instanceof THREE.Line) {
        child.geometry.dispose();
        if (child.material instanceof THREE.Material) {
          child.material.dispose();
        }
      }
    });
    this.group.removeFromParent();
  }

  private stepHuman(dx: number, dy: number): boolean {
    const nx = this.x + dx;
    const ny = this.y + dy;
    if (!isAmbientWalkPos(this.map, nx, ny)) {
      this.wanderAngle += Math.PI * 0.6;
      return false;
    }
    this.x = nx;
    this.y = ny;
    return true;
  }

  private followDog(dtSec: number): void {
    const back = this.facing + Math.PI;
    const idealX = this.x + Math.cos(back) * LEASH_LEN;
    const idealY = this.y + Math.sin(back) * LEASH_LEN;
    const dx = idealX - this.dogX;
    const dy = idealY - this.dogY;
    const dist = Math.hypot(dx, dy);
    if (dist < 0.05) {
      return;
    }
    const pull = Math.min(1, (this.moving ? 2.8 : 1.6) * dtSec);
    const nx = this.dogX + dx * pull;
    const ny = this.dogY + dy * pull;
    if (isAmbientWalkPos(this.map, nx, ny)) {
      this.dogX = nx;
      this.dogY = ny;
    } else if (isAmbientWalkPos(this.map, this.x + Math.cos(back) * 0.7, this.y + Math.sin(back) * 0.7)) {
      this.dogX = this.x + Math.cos(back) * 0.7;
      this.dogY = this.y + Math.sin(back) * 0.7;
    }
  }

  private updateSprites(dtSec: number, now: number, camera: THREE.Camera): void {
    const dogSize = this.art.dogSize(this.fur);
    const dogMoving = Math.hypot(this.dogX - this.x, this.dogY - this.y) > LEASH_LEN * 0.35 || this.moving;

    if (this.moving) {
      this.gaitPhase += dtSec * 2.1 * Math.PI * 2;
      const step = Math.floor((this.gaitPhase / (Math.PI * 2)) * 4) % 4;
      if (step !== this.walkStep) {
        this.walkStep = step;
        const sheet = humanWalkSprite(this.humanVariant, step);
        const mat = this.human.material as THREE.MeshBasicMaterial;
        mat.map = sheet.texture;
        mat.needsUpdate = true;
      }
    } else {
      this.walkStep = -1;
      const mat = this.human.material as THREE.MeshBasicMaterial;
      if (mat.map !== this.idleSheet.texture) {
        mat.map = this.idleSheet.texture;
        mat.needsUpdate = true;
      }
    }

    if (dogMoving) {
      this.gaitPhase += dtSec * 0.4;
      const next = 1 + (Math.floor((this.gaitPhase / (Math.PI * 2)) * 8) % 8);
      if (next !== this.dogFrame) {
        this.dogFrame = next;
        this.applyDogFrame(this.dogFrame, "walk");
      }
    } else {
      const idleFrame = Math.floor(now / 180 + hash(this.id) * 10) % 10;
      if (idleFrame !== this.dogFrame) {
        this.dogFrame = idleFrame;
        this.applyDogFrame(this.dogFrame, "idle");
      }
    }

    const bob = this.moving ? Math.pow(Math.sin(this.gaitPhase), 2) * 0.018 : 0;
    this.human.position.set(0, this.idleSheet.height * this.humanFootY + bob, 0);
    faceCameraYaw(this.human, camera);
    const hFlip = facingFlipSign(this.x, this.y, this.facing, camera);
    this.human.scale.set(hFlip, 1, 1);

    const dogFacing = Math.atan2(this.y - this.dogY, this.x - this.dogX);
    this.dog.position.set(
      this.dogX - this.x,
      dogSize.height * SPRITE_FOOT_Y + (dogMoving ? bob * 0.7 : 0),
      this.dogY - this.y,
    );
    this.dogRim.position.set(this.dog.position.x, this.dog.position.y, this.dog.position.z);
    faceCameraYaw(this.dog, camera);
    const dFlip = facingFlipSign(this.dogX, this.dogY, dogFacing || this.facing, camera);
    this.dog.scale.set(dFlip, 1, 1);
    placeRimBehind(this.dogRim, this.dog, dFlip, 1);

    this.humanShadow.position.set(0, 0.012, 0);
    this.dogShadow.position.set(this.dogX - this.x, 0.012, this.dogY - this.y);
  }

  private applyDogFrame(frame: number, anim: "idle" | "walk"): void {
    const map = this.art.dog(this.fur, null, frame, undefined, anim);
    const mat = this.dog.material as THREE.MeshBasicMaterial;
    mat.map = map;
    mat.needsUpdate = true;
    setSilhouetteMap(this.dogRim, map);
  }

  private updateLeash(): void {
    const handY = this.idleSheet.height * 0.55;
    const dogY = this.art.dogSize(this.fur).height * 0.42;
    const pos = this.leashGeo.attributes.position as THREE.BufferAttribute;
    pos.setXYZ(0, 0.12, handY, 0);
    pos.setXYZ(1, this.dogX - this.x, dogY, this.dogY - this.y);
    pos.needsUpdate = true;
  }

  private place(): void {
    const gy = this.groundY(this.x - 0.5, this.y - 0.5);
    this.group.position.set(this.x, gy, this.y);
  }
}

function snapAmbientHome(map: MapData, x: number, y: number): { x: number; y: number } {
  if (isAmbientWalkPos(map, x, y)) {
    return { x, y };
  }
  const cx = Math.round(x);
  const cy = Math.round(y);
  for (let r = 1; r <= 12; r++) {
    for (let dy = -r; dy <= r; dy++) {
      for (let dx = -r; dx <= r; dx++) {
        if (Math.max(Math.abs(dx), Math.abs(dy)) !== r) {
          continue;
        }
        const px = cx + dx;
        const py = cy + dy;
        if (isAmbientWalkPos(map, px, py)) {
          return { x: px, y: py };
        }
      }
    }
  }
  return { x, y };
}

function hash(seed: string): number {
  let h = 2166136261;
  for (let i = 0; i < seed.length; i++) {
    h ^= seed.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return (h >>> 0) / 4294967295;
}
